import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Mail, Phone, Upload, Download, FileSpreadsheet, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["director","architect","interior_designer","site_engineer","site_supervisor","sales_executive","purchase_manager","accountant","hr","client"];
const empty = { name: "", email: "", phone: "", role: "site_engineer", department: "Operations", salary: 0, status: "active" };
const SAMPLE_CSV = `name,email,phone,role,department,salary,join_date,status
Rohan Desai,rohan@woodland.in,+91 98111 00011,architect,Design,110000,2024-06-01,active
Kavya Iyer,kavya@woodland.in,+91 98111 00012,interior_designer,Design,88000,2024-07-15,active
Manish Gupta,manish@woodland.in,+91 98111 00013,site_engineer,Execution,82000,2024-08-01,active
Priya Menon,priya@woodland.in,+91 98111 00014,sales_executive,Sales,60000,2024-09-01,active
Arjun Nair,arjun@woodland.in,+91 98111 00015,purchase_manager,Procurement,95000,2024-05-15,active
Riya Sharma,riya@woodland.in,+91 98111 00016,accountant,Finance,75000,2024-10-01,active
Sameer Khan,sameer@woodland.in,+91 98111 00017,site_supervisor,Execution,55000,2024-11-01,active
Anjali Kapoor,anjali@woodland.in,+91 98111 00018,hr,HR,70000,2024-04-15,active`;

// Simple CSV parser that handles quoted fields
function parseCSV(text) {
  const lines = text.replace(/\r/g,"").split("\n").filter(l => l.trim());
  if (!lines.length) return [];
  const parseLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"' && line[i+1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQ = !inQ;
      else if (c === "," && !inQ) { out.push(cur); cur = ""; }
      else cur += c;
    }
    out.push(cur); return out;
  };
  const header = parseLine(lines[0]).map(h => h.trim().toLowerCase());
  return lines.slice(1).map(line => {
    const cells = parseLine(line);
    const obj = {};
    header.forEach((h, i) => { obj[h] = (cells[i] || "").trim(); });
    return obj;
  }).filter(r => r.name);
}

export default function Employees() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/employees").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (e) => { setForm({ name:e.name||"", email:e.email||"", phone:e.phone||"", role:e.role||"site_engineer", department:e.department||"Operations", salary:Number(e.salary||0), status:e.status||"active" }); setEditId(e.id); setOpen(true); };
  const add = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editId) { await api.patch(`/employees/${editId}`, form); toast.success("Employee updated"); }
      else { await api.post("/employees", form); toast.success("Employee added"); }
      load(); setOpen(false); setForm(empty); setEditId(null);
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete employee?")) return; await api.delete(`/employees/${id}`); load(); };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const text = await file.text();
      const items = parseCSV(text);
      if (!items.length) { toast.error("No valid rows found"); setImporting(false); return; }
      const { data } = await api.post("/employees/bulk", { items });
      setImportResult(data);
      if (data.ok) toast.success(`Imported ${data.ok} employees`);
      if (data.fail) toast.error(`${data.fail} rows failed`);
      load();
    } catch (err) {
      toast.error("Import failed: " + err.message);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "sample_employees.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">HR</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Employees</h1>
        </div>
        <div className="flex gap-2">
          <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
            <DialogTrigger asChild><Button variant="outline" data-testid="import-csv-btn"><Upload className="w-4 h-4 mr-1.5" />Import CSV</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Import Employees from CSV</DialogTitle></DialogHeader>
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">CSV Format</div>
                <div className="text-xs font-mono">name,email,phone,role,department,salary,join_date,status</div>
                <ul className="text-xs text-muted-foreground space-y-1 pl-2">
                  <li>• Only <strong>name</strong> is required</li>
                  <li>• <strong>role</strong>: {ROLES.slice(0,5).join(", ")}, …</li>
                  <li>• <strong>join_date</strong>: YYYY-MM-DD</li>
                  <li>• <strong>salary</strong>: numeric (₹)</li>
                </ul>
                <Button variant="ghost" size="sm" onClick={downloadSample} className="text-primary p-0 h-auto"><Download className="w-3.5 h-3.5 mr-1" /> Download sample CSV (8 rows)</Button>
              </div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleImport} className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-primary file:text-primary-foreground hover:file:bg-primary/90" data-testid="csv-file-input" />
              {importing && <div className="text-sm text-muted-foreground flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 animate-pulse" /> Importing…</div>}
              {importResult && (
                <div className={`p-3 rounded-lg ${importResult.ok > 0 ? "bg-accent/20 border border-accent/40" : "bg-destructive/10 border border-destructive/30"}`}>
                  <div className="text-sm font-medium">✓ {importResult.ok} imported · ⚠ {importResult.fail} failed</div>
                  {importResult.errors?.length > 0 && (
                    <ul className="text-xs text-destructive mt-2 space-y-0.5">{importResult.errors.map((er, i) => <li key={i}>• {er}</li>)}</ul>
                  )}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground"><span className="opacity-70">ℹ</span> To import from Excel: File → Save As → CSV (Comma delimited)</div>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} data-testid="new-employee-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" />New Employee</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editId ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Input className="col-span-2" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.slice(0,-1).map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
                <Input className="col-span-2" type="number" placeholder="Salary ₹" value={form.salary} onChange={(e) => setForm({ ...form, salary: Number(e.target.value) })} />
              </div>
              <Button onClick={add} data-testid="save-employee-btn" className="bg-primary text-primary-foreground">{editId ? "Update" : "Save"}</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((e) => (
          <div key={e.id} className="rounded-2xl p-6 bg-card border border-border/60 hover:border-primary/40 transition-colors">
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-full bg-accent/20 border border-border/50 flex items-center justify-center text-accent font-display font-semibold">
                {e.name?.slice(0,1).toUpperCase()}
              </div>
              <button onClick={() => remove(e.id)} data-testid={`delete-employee-${e.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 flex items-center justify-between gap-2">
              <div className="font-display text-lg">{e.name}</div>
              <button onClick={() => openEdit(e)} data-testid={`edit-employee-${e.id}`} className="p-1.5 rounded hover:bg-muted text-primary" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
            </div>
            <div className="text-xs text-muted-foreground capitalize">{e.role?.replace("_"," ")} • {e.department}</div>
            <div className="mt-3 space-y-1 text-sm text-muted-foreground">
              {e.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3" />{e.email}</div>}
              {e.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" />{e.phone}</div>}
            </div>
            <div className="mt-4 pt-4 border-t border-border/50 flex justify-between">
              <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Salary</div><div className="text-sm mt-1">₹{Number(e.salary).toLocaleString("en-IN")}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Status</div><div className="text-sm mt-1 capitalize text-accent">{e.status}</div></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
